// Gráfico de barras — previsão de contas a pagar por mês de vencimento,
// a partir dos boletos (parcelas) das notas de entrada. Largura medida
// (viewBox = px reais → sem distorção) e altura fixa.

import { useMemo, useState } from 'react'
import { useContainerWidth } from '../../hooks/useContainerWidth'
import {
  forecastByDueMonth,
  todayLocalISO,
  type BoletoRow,
  type DueMonthBucket,
} from '../../hooks/useCostOfGoods'

interface Props {
  boletos: BoletoRow[]
}

const HEIGHT = 280
const PAD_L = 52
const PAD_R = 16
const PAD_T = 28
const PAD_B = 34

function brlShort(n: number): string {
  if (Math.abs(n) >= 1_000_000) return `R$ ${(n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`
  if (Math.abs(n) >= 1_000) return `R$ ${Math.round(n / 1_000)}k`
  return `R$ ${Math.round(n)}`
}
function brl(n: number): string {
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 })
}

// Verde da marca (a vencer) validado nos dois temas via par light/dark.
const FILL_AVENCER = 'fill-[#2f8a4e] dark:fill-[#34a862]'
const FILL_PASSADO = 'fill-ink-faint'

export default function ContasPagarForecastChart({ boletos }: Props) {
  const [hover, setHover] = useState<number | null>(null)
  const [ref, W] = useContainerWidth<HTMLDivElement>()
  const today = todayLocalISO()

  const data = useMemo<DueMonthBucket[]>(
    () => forecastByDueMonth(boletos, today),
    [boletos, today],
  )
  const aVencer = useMemo(
    () => data.filter(d => d.bucket !== 'passado').reduce((s, d) => s + d.total, 0),
    [data],
  )

  const innerW = Math.max(W - PAD_L - PAD_R, 0)
  const innerH = HEIGHT - PAD_T - PAD_B

  const maxTotal = Math.max(...data.map(d => d.total), 1)
  const yMax = niceCeil(maxTotal)
  const yTicks = [0, yMax * 0.25, yMax * 0.5, yMax * 0.75, yMax]

  const n = data.length || 1
  const band = innerW / n
  const barW = Math.min(band * 0.62, 64)
  const xCenter = (i: number) => PAD_L + band * i + band / 2
  const yOf = (v: number) => PAD_T + innerH - (v / yMax) * innerH

  const firstFutureIdx = data.findIndex(d => d.bucket !== 'passado')
  const dividerX = firstFutureIdx > 0 ? PAD_L + band * firstFutureIdx : null
  const showTopLabels = data.length <= 10

  // Afina os rótulos do eixo X p/ não encavalar: passo em função da largura da
  // faixa (band). Ancorado no ÚLTIMO mês (o mais recente sempre aparece).
  const lastIdx = data.length - 1
  const xLabelStep = Math.max(1, Math.ceil(40 / Math.max(band, 1)))

  return (
    <div className="bg-surface border border-line rounded-xl p-4 sm:p-5 flex flex-col">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-ink">Previsão de contas a pagar</h3>
        <div className="flex gap-3 text-xs text-ink-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#2f8a4e] dark:bg-[#34a862]" /> a vencer
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-ink-faint" /> já vencidos
          </span>
        </div>
      </div>
      <p className="text-xs text-ink-faint mt-0.5 mb-2">
        Por mês de vencimento · a vencer: <span className="text-ink font-medium tabular-nums">{brl(aVencer)}</span>
      </p>

      <div ref={ref} className="relative w-full" style={{ height: HEIGHT }}>
        {data.length === 0 ? (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-ink-faint text-center px-4">
            Sem boletos com vencimento. Use “↻ Sincronizar notas do Tiny” para puxar as parcelas.
          </div>
        ) : W > 0 && (
          <svg viewBox={`0 0 ${W} ${HEIGHT}`} className="w-full block" role="img">
            {/* Grid + labels Y */}
            {yTicks.map((t, idx) => (
              <g key={idx}>
                <line x1={PAD_L} x2={W - PAD_R} y1={yOf(t)} y2={yOf(t)} className="stroke-line" strokeWidth="1" />
                <text x={PAD_L - 8} y={yOf(t) + 4} className="fill-ink-muted" fontSize="11" textAnchor="end">
                  {brlShort(t)}
                </text>
              </g>
            ))}

            {/* Separador "hoje" */}
            {dividerX != null && (
              <g>
                <line x1={dividerX} x2={dividerX} y1={PAD_T} y2={PAD_T + innerH} stroke="#f87171" strokeWidth="1.5" strokeDasharray="4 4" />
                <text x={dividerX + 4} y={PAD_T + 10} className="fill-danger" fontSize="10" textAnchor="start">hoje</text>
              </g>
            )}

            {/* Barras */}
            {data.map((d, i) => {
              const x = xCenter(i) - barW / 2
              const y = yOf(d.total)
              const h = Math.max(PAD_T + innerH - y, d.total > 0 ? 2 : 0)
              const isHover = hover === i
              const fill = d.bucket === 'passado' ? FILL_PASSADO : FILL_AVENCER
              return (
                <g key={d.ym}>
                  <rect
                    x={x} y={y} width={barW} height={h} rx="5" ry="5"
                    className={`${fill} transition-opacity ${isHover ? 'opacity-100' : d.bucket === 'passado' ? 'opacity-60' : 'opacity-95'}`}
                  />
                  <rect
                    x={PAD_L + band * i} y={PAD_T} width={band} height={innerH}
                    fill="transparent"
                    onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
                  />
                  {showTopLabels && d.total > 0 && (
                    <text x={xCenter(i)} y={y - 6} className="fill-ink-muted tabular-nums" fontSize="10" textAnchor="middle">
                      {brlShort(d.total)}
                    </text>
                  )}
                </g>
              )
            })}

            {/* Labels X (afinados p/ não encavalar) */}
            {data.map((d, i) => {
              if ((lastIdx - i) % xLabelStep !== 0) return null
              return (
                <text
                  key={`x-${d.ym}`}
                  x={xCenter(i)} y={HEIGHT - PAD_B + 20}
                  className={d.bucket === 'atual' ? 'fill-ink font-semibold' : 'fill-ink-muted'}
                  fontSize="11" textAnchor="middle"
                >
                  {d.label}
                </text>
              )
            })}
          </svg>
        )}

        {/* Tooltip */}
        {hover !== null && data[hover] && W > 0 && (
          <div
            className="absolute bg-surface border border-line rounded-lg px-3 py-2 text-xs pointer-events-none shadow-lg z-10"
            style={{ left: `${(xCenter(hover) / W) * 100}%`, top: 0, transform: 'translateX(-50%)' }}
          >
            <div className="font-semibold text-ink whitespace-nowrap">{data[hover].label}</div>
            <div className="text-ink-muted mt-1 space-y-0.5">
              <div className="whitespace-nowrap">A pagar: <span className="text-ink font-medium">{brl(data[hover].total)}</span></div>
              <div>{data[hover].count} boleto(s)</div>
              <div>
                {data[hover].bucket === 'passado' ? 'Vencidos'
                  : data[hover].bucket === 'atual' ? 'Vence este mês'
                  : 'A vencer'}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// Arredonda o topo do eixo para um número "redondo" (1/2/2.5/5 × 10^n).
function niceCeil(v: number): number {
  if (v <= 0) return 1
  const exp = Math.floor(Math.log10(v))
  const base = Math.pow(10, exp)
  const f = v / base
  const nice = f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10
  return nice * base
}
